import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Grid,
  Radio,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  AppBar,
  Toolbar,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CloudIcon from "@mui/icons-material/Cloud";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import BarChartIcon from "@mui/icons-material/BarChart";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  SubscriptionPlan,
  useGetSubscriptionPlansQuery,
  useSignupMutation,
  useLazyCheckTenantNameQuery,
  useLazyCheckEmailQuery,
} from "../../api/subscriptionApi";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import PlanSectionsList from "../../components/subscription/PlanSectionsList";

const steps = ["Choose plan", "Company info", "Payment method"];

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const { data: plans, isLoading: isLoadingPlans, error: plansError } =
    useGetSubscriptionPlansQuery();
  const [signup, { isLoading: isSigningUp }] = useSignupMutation();
  const [checkTenantName] = useLazyCheckTenantNameQuery();
  const [checkEmail] = useLazyCheckEmailQuery();
  const stripe = useStripe();
  const elements = useElements();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [vehicleCount, setVehicleCount] = useState<number>(1);

  const [tenantName, setTenantName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [tenantNameError, setTenantNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isCheckingTenantName, setIsCheckingTenantName] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const tenantNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedPlan: SubscriptionPlan | undefined = useMemo(
    () => plans?.find((p) => p.id === selectedPlanId),
    [plans, selectedPlanId]
  );

  const sortedPlans = useMemo(
    () => plans ? [...plans].sort((a, b) => a.pricePerVehicle - b.pricePerVehicle) : [],
    [plans]
  );

  // Validate tenant name with debounce
  useEffect(() => {
    if (tenantNameTimeoutRef.current) {
      clearTimeout(tenantNameTimeoutRef.current);
    }

    if (!tenantName.trim()) {
      setTenantNameError(null);
      setIsCheckingTenantName(false);
      return;
    }

    setIsCheckingTenantName(true);
    tenantNameTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await checkTenantName(tenantName.trim()).unwrap();
        if (!result) {
          setTenantNameError("A company with this name already exists. Please choose a different name.");
        } else {
          setTenantNameError(null);
        }
      } catch (err) {
        // If check fails, don't block the user - backend will validate anyway
        setTenantNameError(null);
      } finally {
        setIsCheckingTenantName(false);
      }
    }, 300);

    return () => {
      if (tenantNameTimeoutRef.current) {
        clearTimeout(tenantNameTimeoutRef.current);
      }
    };
  }, [tenantName, checkTenantName]);

  // Validate email with debounce
  useEffect(() => {
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    if (!contactEmail.trim()) {
      setEmailError(null);
      setIsCheckingEmail(false);
      return;
    }

    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail.trim())) {
      setEmailError(null); // Let the form validation handle format errors
      setIsCheckingEmail(false);
      return;
    }

    setIsCheckingEmail(true);
    emailTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await checkEmail(contactEmail.trim()).unwrap();

        if (result === false) {
          setEmailError("This email is already registered. Please use a different email address.");
        } else if (result === true) {
          setEmailError(null);
        }
      } catch (err: any) {
        // If check fails, set error to indicate validation issue
        const errorMessage = err?.data?.message || err?.message || "Unable to verify email availability. Please try again.";
        setEmailError(errorMessage);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 300);

    return () => {
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, [contactEmail, checkEmail]);

  const handleNext = async () => {
    setErrorMessage(null);

    if (activeStep === 0) {
      if (!selectedPlanId) {
        setErrorMessage("Please select a subscription plan to continue.");
        return;
      }
      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      if (!tenantName.trim()) {
        setErrorMessage("Company name is required.");
        return;
      }
      if (tenantNameError) {
        setErrorMessage(tenantNameError);
        return;
      }
      if (isCheckingTenantName) {
        setErrorMessage("Please wait while we check if the company name is available...");
        return;
      }
      // Email is required for admin account creation
      if (!contactEmail.trim()) {
        setErrorMessage("Contact email is required. This will be used as your admin account email.");
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactEmail.trim())) {
        setErrorMessage("Please enter a valid email address.");
        return;
      }
      // Always check email availability before proceeding
      if (isCheckingEmail) {
        setErrorMessage("Please wait while we check if the email is available...");
        return;
      }

      // Force a final check - must wait for result
      try {
        const emailCheckResult = await checkEmail(contactEmail.trim()).unwrap();

        if (emailCheckResult === false) {
          setEmailError("This email is already registered. Please use a different email address.");
          setErrorMessage("This email is already registered. Please use a different email address.");
          return;
        }

        if (emailCheckResult === true) {
          // Email is available
          setEmailError(null);
        } else {
          // Unknown state
          setErrorMessage("Unable to verify email availability. Please try again.");
          return;
        }
      } catch (err: any) {
        // If check fails, block the user - we cannot proceed without validation
        const errorMessage = err?.data?.message || err?.message || "Unable to verify email availability. Please try again.";
        setErrorMessage(errorMessage);
        return;
      }

      // Double check - if there's still an error state, block
      if (emailError) {
        setErrorMessage(emailError);
        return;
      }
      if (!vehicleCount || vehicleCount <= 0) {
        setErrorMessage("Number of vehicles must be at least 1.");
        return;
      }
      setActiveStep(2);
      return;
    }

    if (activeStep === 2) {
      if (!selectedPlanId) {
        setErrorMessage("Please select a subscription plan.");
        setActiveStep(0);
        return;
      }

      // Validate email again before final submission
      if (!contactEmail.trim()) {
        setErrorMessage("Contact email is required. This will be used as your admin account email.");
        setActiveStep(1);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactEmail.trim())) {
        setErrorMessage("Please enter a valid email address.");
        setActiveStep(1);
        return;
      }

      // Final email availability check before submission - MUST pass
      try {
        const emailCheckResult = await checkEmail(contactEmail.trim()).unwrap();

        if (emailCheckResult === false) {
          setEmailError("This email is already registered. Please use a different email address.");
          setErrorMessage("This email is already registered. Please use a different email address.");
          setActiveStep(1);
          return;
        }

        if (emailCheckResult !== true) {
          // Unknown state
          setErrorMessage("Unable to verify email availability. Please try again.");
          setActiveStep(1);
          return;
        }
      } catch (err: any) {
        // If check fails, block submission - we cannot proceed without validation
        const errorMessage = err?.data?.message || err?.message || "Unable to verify email availability. Please try again or use a different email.";
        setErrorMessage(errorMessage);
        setActiveStep(1);
        return;
      }

      if (!stripe || !elements) {
        setErrorMessage("Payment system is not ready. Please try again.");
        return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setErrorMessage("Unable to find card input.");
        return;
      }

      try {
        const pmResult = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: tenantName || undefined,
            email: contactEmail || undefined,
          },
        });

        if (pmResult.error || !pmResult.paymentMethod) {
          setErrorMessage(
            pmResult.error?.message || "Failed to create payment method."
          );
          return;
        }

        const result = await signup({
          subscriptionPlanId: selectedPlanId,
          tenantName: tenantName.trim(),
          contactEmail: contactEmail.trim(),
          contactPhone: contactPhone.trim() || undefined,
          vehicleCount,
          paymentMethodId: pmResult.paymentMethod.id,
        }).unwrap();

        setSuccessMessage(
          `Your ${result.planName} plan trial has started. Trial duration: ${result.trialPeriodDays} days.`
        );
        setActiveStep(steps.length);
      } catch (err: any) {
        // Handle backend validation errors
        const errors = err?.data?.errors || [];
        const errorMessages = err?.data?.message
          ? [err.data.message]
          : errors.length > 0
            ? errors
            : [err?.error || "Signup failed."];

        // Check for specific validation errors
        const errorText = errorMessages.join(", ");
        if (errorText.toLowerCase().includes("tenant") && errorText.toLowerCase().includes("name")) {
          setTenantNameError("A company with this name already exists. Please choose a different name.");
          setErrorMessage(null);
        } else if (errorText.toLowerCase().includes("email") && (errorText.toLowerCase().includes("already") || errorText.toLowerCase().includes("registered"))) {
          setEmailError("This email is already registered. Please use a different email address.");
          setErrorMessage(null);
        } else {
          setErrorMessage(errorText);
        }
      }
    }
  };

  const handleBackStep = () => {
    setErrorMessage(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const renderPlanStep = () => {
    if (isLoadingPlans) {
      return (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (plansError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load subscription plans.
        </Alert>
      );
    }

    if (!plans || plans.length === 0) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No subscription plans are currently available. Please contact support.
        </Alert>
      );
    }

    return (
      <Grid container spacing={2.5} mt={1}>
        {sortedPlans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: selectedPlanId === plan.id
                  ? "rgba(220, 38, 38, 0.08)"
                  : "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                border: selectedPlanId === plan.id
                  ? "2px solid #dc2626"
                  : "1px solid rgba(100, 116, 139, 0.2)",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  borderColor: selectedPlanId === plan.id ? "#dc2626" : "rgba(220, 38, 38, 0.5)",
                  background: selectedPlanId === plan.id
                    ? "rgba(220, 38, 38, 0.12)"
                    : "rgba(255, 255, 255, 0.9)",
                  boxShadow: "0 20px 40px rgba(220, 38, 38, 0.15)",
                },
              }}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              <CardContent sx={{ p: 3.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    {plan.name}
                  </Typography>
                  <Radio
                    checked={selectedPlanId === plan.id}
                    onChange={() => setSelectedPlanId(plan.id)}
                    sx={{
                      color: "rgba(30, 41, 59, 0.3)",
                      "&.Mui-checked": {
                        color: "#dc2626",
                      },
                    }}
                  />
                </Box>

                <Chip
                  label={plan.code}
                  size="small"
                  sx={{
                    mb: 3,
                    backgroundColor: "rgba(220, 38, 38, 0.15)",
                    color: "#dc2626",
                    fontWeight: 600,
                    fontSize: "12px",
                    height: "24px",
                  }}
                />

                <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5, letterSpacing: "-1px" }}>
                  ${plan.pricePerVehicle.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                  per vehicle / month
                </Typography>

                {plan.trialPeriodDays > 0 && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(220, 38, 38, 0.1)",
                      borderRadius: "8px",
                      p: 1.5,
                      mb: 2,
                      border: "1px solid rgba(220, 38, 38, 0.2)"
                    }}
                  >
                    <Typography sx={{ color: "#dc2626", fontWeight: 600, fontSize: "14px" }}>
                      ✓ {plan.trialPeriodDays}-day free trial
                    </Typography>
                  </Box>
                )}

                {plan.description && (
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 2, lineHeight: 1.6 }}>
                    {plan.description}
                  </Typography>
                )}

                <PlanSectionsList planCode={plan.code} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderTenantStep = () => (
    <Box mt={2}>
      <TextField
        fullWidth
        label="Company name"
        margin="normal"
        value={tenantName}
        onChange={(e) => {
          setTenantName(e.target.value);
          setTenantNameError(null);
        }}
        required
        error={!!tenantNameError}
        helperText={tenantNameError || (isCheckingTenantName ? "Checking availability..." : "")}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(241, 245, 249, 0.8)",
            borderRadius: "12px",
            "& fieldset": {
              borderColor: tenantNameError ? "#dc2626" : "rgba(100, 116, 139, 0.2)",
            },
            "&:hover fieldset": {
              borderColor: tenantNameError ? "#dc2626" : "rgba(100, 116, 139, 0.4)",
            },
            "&.Mui-focused fieldset": {
              borderColor: tenantNameError ? "#dc2626" : "#dc2626",
              borderWidth: "2px",
            },
            "& input": {
              color: "#1e293b",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#64748b",
            "&.Mui-focused": {
              color: tenantNameError ? "#dc2626" : "#dc2626",
            },
          },
          "& .MuiFormHelperText-root": {
            color: tenantNameError ? "#dc2626" : isCheckingTenantName ? "#64748b" : undefined,
            fontWeight: tenantNameError ? 600 : undefined,
          },
        }}
      />
      <TextField
        fullWidth
        label="Contact email (Admin email)"
        margin="normal"
        type="email"
        value={contactEmail}
        onChange={(e) => {
          setContactEmail(e.target.value);
          setEmailError(null);
        }}
        required
        error={!!emailError}
        helperText={emailError || (isCheckingEmail ? "Checking availability..." : "This will be used as the admin account email")}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(241, 245, 249, 0.8)",
            borderRadius: "12px",
            "& fieldset": {
              borderColor: emailError ? "#dc2626" : "rgba(100, 116, 139, 0.2)",
            },
            "&:hover fieldset": {
              borderColor: emailError ? "#dc2626" : "rgba(100, 116, 139, 0.4)",
            },
            "&.Mui-focused fieldset": {
              borderColor: emailError ? "#dc2626" : "#dc2626",
              borderWidth: "2px",
            },
            "& input": {
              color: "#1e293b",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#64748b",
            "&.Mui-focused": {
              color: emailError ? "#dc2626" : "#dc2626",
            },
          },
          "& .MuiFormHelperText-root": {
            color: emailError ? "#dc2626" : isCheckingEmail ? "#64748b" : "#64748b",
            fontWeight: emailError ? 600 : undefined,
          },
        }}
      />
      <TextField
        fullWidth
        label="Contact phone"
        margin="normal"
        value={contactPhone}
        onChange={(e) => setContactPhone(e.target.value)}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(241, 245, 249, 0.8)",
            borderRadius: "12px",
            "& fieldset": {
              borderColor: "rgba(100, 116, 139, 0.2)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(100, 116, 139, 0.4)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#dc2626",
              borderWidth: "2px",
            },
            "& input": {
              color: "#1e293b",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#64748b",
            "&.Mui-focused": {
              color: "#dc2626",
            },
          },
        }}
      />
      <TextField
        fullWidth
        type="number"
        label="Number of vehicles"
        margin="normal"
        value={vehicleCount}
        onChange={(e) => setVehicleCount(Number(e.target.value) || 0)}
        inputProps={{ min: 1 }}
        required
        helperText={
          selectedPlan
            ? `Estimated monthly price: $${(
              selectedPlan.pricePerVehicle * Math.max(vehicleCount || 0, 0)
            ).toFixed(2)}`
            : undefined
        }
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(241, 245, 249, 0.8)",
            borderRadius: "12px",
            "& fieldset": {
              borderColor: "rgba(100, 116, 139, 0.2)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(100, 116, 139, 0.4)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#dc2626",
              borderWidth: "2px",
            },
            "& input": {
              color: "#1e293b",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#64748b",
            "&.Mui-focused": {
              color: "#dc2626",
            },
          },
          "& .MuiFormHelperText-root": {
            color: "#dc2626",
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );

  const renderPaymentStep = () => (
    <Box mt={2}>
      {selectedPlan && (
        <Box
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "16px",
            background: "rgba(220, 38, 38, 0.08)",
            border: "1px solid rgba(220, 38, 38, 0.2)",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#64748b", mb: 1 }}>
            Selected Plan
          </Typography>
          <Typography variant="h5" sx={{ color: "#1e293b", fontWeight: 700, mb: 1, letterSpacing: "-0.5px" }}>
            {selectedPlan.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
            <Typography variant="h4" sx={{ color: "#dc2626", fontWeight: 700, letterSpacing: "-1px" }}>
              ${(selectedPlan.pricePerVehicle * Math.max(vehicleCount || 0, 0)).toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              / month
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            ${selectedPlan.pricePerVehicle.toFixed(2)} per vehicle × {vehicleCount} vehicle{vehicleCount !== 1 ? "s" : ""}
          </Typography>
        </Box>
      )}
      <Box mt={2}>
        <Typography variant="subtitle1" sx={{ color: "#1e293b", fontWeight: 600, mb: 2, letterSpacing: "-0.5px" }}>
          Payment Details
        </Typography>
        <Box
          sx={{
            p: 2.5,
            borderRadius: "12px",
            border: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(241, 245, 249, 0.8)",
            transition: "all 0.3s ease",
            "&:focus-within": {
              borderColor: "#dc2626",
              borderWidth: "2px",
              p: "9px",
            },
          }}
        >
          <CardElement options={{
            ...cardElementOptions,
            style: {
              base: {
                fontSize: "16px",
                color: "#1e293b",
                "::placeholder": {
                  color: "#94a3b8",
                },
              },
              invalid: {
                color: "#dc2626",
              },
            },
          }} />
        </Box>
        <Typography variant="caption" sx={{ color: "#64748b", mt: 1.5, display: "block" }}>
          🔒 Secure payment powered by Stripe.
        </Typography>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (activeStep === 0) return renderPlanStep();
    if (activeStep === 1) return renderTenantStep();
    if (activeStep === 2) return renderPaymentStep();

    return (
      <Box
        mt={2}
        sx={{
          textAlign: "center",
          p: 4,
          borderRadius: "16px",
          background: "rgba(220, 38, 38, 0.05)",
          border: "1px solid rgba(220, 38, 38, 0.2)",
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 60, color: "#dc2626", mb: 2 }} />
        <Typography variant="h4" sx={{ color: "#1e293b", fontWeight: 700, mb: 2, letterSpacing: "-0.5px" }}>
          Welcome to ADUT! 🎉
        </Typography>
        <Typography variant="body1" sx={{ color: "#64748b", lineHeight: 1.6 }}>
          Your account has been successfully created. You can now log in to your account once your user is set up for this tenant.
        </Typography>
      </Box>
    );
  };

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        background: "linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "url('/images/truck-outline.png')",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.06,
          zIndex: 0,
        },
      }}
    >
      {/* Navigation Bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(220, 38, 38, 0.1)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box
            component="img"
            src="/images/adut-logo.svg"
            alt="ADUT Logo"
            sx={{
              height: 50,
              mr: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              color: "#1e293b",
              fontWeight: 600,
              letterSpacing: "-0.5px"
            }}
          >
            ADUT Fleet Management
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/login")}
            sx={{
              color: "#dc2626",
              borderColor: "#dc2626",
              borderRadius: "10px",
              px: 3,
              py: 1,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#dc2626",
                backgroundColor: "rgba(220, 38, 38, 0.1)",
                transform: "translateY(-1px)",
              },
            }}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6, position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: activeStep === 0 ? 8 : 4 }}>
          <Typography
            variant={activeStep === 0 ? "h1" : "h3"}
            sx={{
              color: "white",
              fontWeight: 700,
              mb: activeStep === 0 ? 3 : 2,
              fontSize: activeStep === 0
                ? { xs: "2.5rem", md: "4rem" }
                : { xs: "1.8rem", md: "2.5rem" },
              letterSpacing: activeStep === 0 ? "-2px" : "-1px",
              lineHeight: 1.1,
              transition: "all 0.3s ease",
            }}
          >
            {activeStep === 0 ? (
              <>
                Transform Your Fleet
                <br />
                <Box component="span" sx={{ color: "#dc2626" }}>
                  Management
                </Box>
              </>
            ) : (
              <>
                Start Your{" "}
                <Box component="span" sx={{ color: "#dc2626" }}>
                  Free Trial
                </Box>
              </>
            )}
          </Typography>

          <Typography
            variant={activeStep === 0 ? "h5" : "body1"}
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              mb: activeStep === 0 ? 6 : 3,
              fontWeight: activeStep === 0 ? 400 : 400,
              fontSize: activeStep === 0 ? { xs: "1.1rem", md: "1.5rem" } : "1.1rem",
              letterSpacing: activeStep === 0 ? "-0.5px" : "normal",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Real-time tracking, analytics, and optimization for modern fleets
          </Typography>

          {activeStep === 0 && (
            /* Benefits Grid */
            <Grid container spacing={2.5} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: "1px solid rgba(220, 38, 38, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      background: "rgba(255, 255, 255, 1)",
                      borderColor: "rgba(220, 38, 38, 0.5)",
                      boxShadow: "0 20px 40px rgba(220, 38, 38, 0.2)",
                    },
                  }}
                >
                  <SpeedIcon sx={{ fontSize: 40, color: "#dc2626", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    Real-Time Tracking
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                    Monitor your entire fleet in real-time with GPS tracking, live updates, and instant notifications
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: "1px solid rgba(220, 38, 38, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      background: "rgba(255, 255, 255, 1)",
                      borderColor: "rgba(220, 38, 38, 0.5)",
                      boxShadow: "0 20px 40px rgba(220, 38, 38, 0.2)",
                    },
                  }}
                >
                  <EventAvailableIcon sx={{ fontSize: 40, color: "#dc2626", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    Absence Management
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                    Track employee time off, vacation days, sick leaves, and manage absence requests with automated approval workflows
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: "1px solid rgba(220, 38, 38, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      background: "rgba(255, 255, 255, 1)",
                      borderColor: "rgba(220, 38, 38, 0.5)",
                      boxShadow: "0 20px 40px rgba(220, 38, 38, 0.2)",
                    },
                  }}
                >
                  <CloudIcon sx={{ fontSize: 40, color: "#dc2626", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    Cloud-Based Platform
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                    Access your data anywhere, anytime with our reliable cloud infrastructure and automatic backups
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: "1px solid rgba(220, 38, 38, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      background: "rgba(255, 255, 255, 1)",
                      borderColor: "rgba(220, 38, 38, 0.5)",
                      boxShadow: "0 20px 40px rgba(220, 38, 38, 0.2)",
                    },
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 40, color: "#dc2626", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    Easy Integration
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                    Seamlessly integrate with your existing systems and third-party applications
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: "1px solid rgba(220, 38, 38, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      background: "rgba(255, 255, 255, 1)",
                      borderColor: "rgba(220, 38, 38, 0.5)",
                      boxShadow: "0 20px 40px rgba(220, 38, 38, 0.2)",
                    },
                  }}
                >
                  <SupportAgentIcon sx={{ fontSize: 40, color: "#dc2626", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    24/7 Support
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                    Dedicated support team ready to assist you at any time with any questions
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: "1px solid rgba(220, 38, 38, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      background: "rgba(255, 255, 255, 1)",
                      borderColor: "rgba(220, 38, 38, 0.5)",
                      boxShadow: "0 20px 40px rgba(220, 38, 38, 0.2)",
                    },
                  }}
                >
                  <BarChartIcon sx={{ fontSize: 40, color: "#dc2626", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: "#1e293b", letterSpacing: "-0.5px" }}>
                    Advanced Analytics
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                    Make data-driven decisions with comprehensive reports and actionable insights
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>

      {/* Signup Form */}
      <Container component="main" maxWidth="md" sx={{ pb: 8, pt: activeStep === 0 ? 0 : 4, position: "relative", zIndex: 1 }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "24px",
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(40px)",
            border: "1px solid rgba(220, 38, 38, 0.1)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                width: "100%",
                mb: 4,
                "& .MuiStepLabel-root .Mui-completed": {
                  color: "#dc2626",
                },
                "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
                  color: "#64748b",
                },
                "& .MuiStepLabel-root .Mui-active": {
                  color: "#dc2626",
                },
                "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel": {
                  color: "#1e293b",
                  fontWeight: 600,
                },
                "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
                  fill: "white",
                },
                "& .MuiStepLabel-label": {
                  color: "#94a3b8",
                },
                "& .MuiStepIcon-root": {
                  color: "#cbd5e1",
                },
                "& .MuiStepConnector-line": {
                  borderColor: "#cbd5e1",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2, width: "100%" }}>
                {successMessage}
              </Alert>
            )}

            <Box sx={{ width: "100%" }}>{renderContent()}</Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 5,
                width: "100%",
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/login")}
                disabled={isSigningUp}
                sx={{
                  color: "#64748b",
                  textTransform: "none",
                  fontSize: "15px",
                  "&:hover": {
                    color: "#1e293b",
                    backgroundColor: "transparent",
                  }
                }}
              >
                Already have an account? Login
              </Button>

              <Box>
                {activeStep > 0 && activeStep < steps.length && (
                  <Button
                    variant="outlined"
                    onClick={handleBackStep}
                    disabled={isSigningUp}
                    sx={{
                      mr: 2,
                      color: "#64748b",
                      borderColor: "rgba(100, 116, 139, 0.3)",
                      borderRadius: "10px",
                      px: 3,
                      py: 1.2,
                      textTransform: "none",
                      fontSize: "16px",
                      fontWeight: 500,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#64748b",
                        backgroundColor: "rgba(100, 116, 139, 0.05)",
                        color: "#1e293b",
                      },
                      "&:disabled": {
                        borderColor: "rgba(100, 116, 139, 0.2)",
                        color: "rgba(100, 116, 139, 0.4)",
                      }
                    }}
                  >
                    Back
                  </Button>
                )}
                {activeStep < steps.length && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isSigningUp}
                    sx={{
                      background: "#dc2626",
                      color: "white",
                      borderRadius: "10px",
                      px: 4,
                      py: 1.2,
                      textTransform: "none",
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "0 4px 14px 0 rgba(220, 38, 38, 0.4)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "#b91c1c",
                        boxShadow: "0 6px 20px 0 rgba(220, 38, 38, 0.6)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        background: "rgba(220, 38, 38, 0.3)",
                      }
                    }}
                  >
                    {isSigningUp ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : isLastStep ? (
                      "Start Free Trial"
                    ) : (
                      "Continue"
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

const SignupPage: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <SignupForm />
    </Elements>
  );
};

export default SignupPage;
